/**
 * @file Main Tasks page visibility horizon helpers.
 */

import { state, appData } from '../../app/state/state.js';
import { getLocalDateString } from './dateUtils.js';

/**
 * Parses a task visibility horizon value into a whole-day count.
 * @param {*} value Configured or user-saved horizon value.
 * @returns {number | null} Valid horizon days, or null when unset/invalid.
 */
export function parseTaskHorizonDays(value) {
    if (value === null || value === undefined || value === '') {return null;}
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {return null;}
    return Math.floor(parsed);
}

/**
 * Returns the configured minimum task visibility horizon.
 * @returns {number | null} Minimum horizon days, or null when unset.
 */
export function getTaskHorizonMinimumDays() {
    return parseTaskHorizonDays(appData.config?.minTaskVisibilityHorizonDays);
}

/**
 * Resolves the active user's effective task visibility horizon.
 * @returns {number | null} Effective horizon days, or null when disabled.
 */
export function getCurrentUserTaskHorizonDays() {
    const userValue = parseTaskHorizonDays(state.currentUserProfile?.taskVisibilityHorizonDays);
    if (userValue === null) {return null;}

    const minimum = getTaskHorizonMinimumDays();
    return minimum === null ? userValue : Math.max(userValue, minimum);
}

function parseDateOnly(dateString) {
    if (!dateString) {return null;}
    const parsed = new Date(`${dateString}T00:00:00`);
    if (isNaN(parsed.getTime())) {return null;}
    parsed.setHours(0, 0, 0, 0);
    return parsed;
}

/**
 * Filters tasks to those visible within the current user's horizon.
 * @param {Array<object>} tasks Task records to filter.
 * @returns {Array<object>} Tasks visible under the active horizon settings.
 */
export function filterTasksByVisibilityHorizon(tasks = []) {
    const horizonDays = getCurrentUserTaskHorizonDays();
    if (horizonDays === null) {return tasks;}

    const today = parseDateOnly(getLocalDateString()) || new Date();
    today.setHours(0, 0, 0, 0);

    const maxVisibleDate = new Date(today);
    maxVisibleDate.setDate(maxVisibleDate.getDate() + horizonDays);

    return tasks.filter((task) => {
        const dueDate = parseDateOnly(task?.due_date);
        if (!dueDate) {return true;}
        if (dueDate < today) {return true;}
        return dueDate <= maxVisibleDate;
    });
}
